import { useRef, useState } from "react";
import { Camera, ArrowRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

// Étape obligatoire après le quiz : la photo de profil (anti faux profils)
export const ProfileSetup = ({ user, setUser }) => {
  const [avatar, setAvatar] = useState(null);
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/profile/avatar", fd);
      setAvatar(data.avatar);
      toast.success("Belle photo ! Elle est vérifiée et enregistrée.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!avatar) {
      toast.error("Une photo de profil est obligatoire pour rencontrer les autres membres.");
      return;
    }
    setSaving(true);
    try {
      if (bio.trim()) await api.patch("/profile/me", { bio: bio.trim() });
      setUser({ ...user, avatar, bio: bio.trim() || user.bio });
      toast.success("Votre carte de visite est prête !");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative flex min-h-[calc(100vh-64px)] items-center justify-center overflow-hidden px-4 py-16" data-testid="profile-setup">
      <img src="/assets/mandala-sun.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.22]" />
      <div className="absolute inset-0 bg-gradient-to-b from-rosedust/80 via-cream/70 to-rosedust/80" />
      <div className="relative w-full max-w-md rounded-3xl border-2 border-gold/60 bg-white/95 p-8 text-center shadow-card backdrop-blur-md sm:p-10">
        <p className="font-cinzel text-xs tracking-[0.35em] text-gold">DERNIÈRE ÉTAPE</p>
        <h1 className="mt-3 font-serif text-2xl font-semibold text-ink sm:text-3xl">Votre carte de visite</h1>
        <p className="mt-3 text-sm leading-relaxed text-clay">
          Pour découvrir vos affinités et être visible des autres membres, ajoutez votre photo — elle est <strong>obligatoire</strong> et nous aide à garder une communauté bienveillante, sans faux profils.
        </p>
        <div className="mt-7 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => !uploading && fileRef.current?.click()}
            disabled={uploading}
            data-testid="setup-avatar-button"
            className="relative flex h-32 w-32 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-terra/50 bg-parch transition-all duration-300 hover:border-terra hover:scale-[1.03] disabled:cursor-wait disabled:opacity-60"
          >
            {avatar ? (
              <img src={avatar} alt="Votre photo" className="h-full w-full object-cover" />
            ) : (
              <span className="flex flex-col items-center gap-1.5 text-terra">
                <Camera size={26} />
                <span className="text-[10px] font-semibold uppercase tracking-widest">{uploading ? "Analyse…" : "Ma photo"}</span>
              </span>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="setup-avatar-input" onChange={uploadAvatar} />
          <p className="text-[11px] leading-snug text-stone2">Une photo de vous, <strong>seul·e</strong>, visage bien visible — vérifiée automatiquement pour éviter les faux profils.</p>
        </div>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          data-testid="setup-bio-input"
          rows={3}
          maxLength={300}
          placeholder="Quelques mots sur vous (facultatif) : ce que vous aimez, ce qui vous fait sourire…"
          className="mt-5 w-full rounded-2xl border border-terra/20 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-terra"
        />
        <button
          onClick={save}
          disabled={saving}
          data-testid="setup-save-button"
          className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-terra px-6 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark disabled:opacity-50"
        >
          {saving ? "Enregistrement…" : "Découvrir mon résultat"}
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>
    </div>
  );
};
